/**
 * Return `timeZone` if it is a valid IANA time zone, otherwise `undefined`.
 *
 * A stale/malformed timezone string (e.g. a legacy label like
 * "America/New_York - EDT (EDT)" cached in localStorage) makes date-fns-tz
 * throw `RangeError: Invalid time value` downstream. Guarding here keeps a bad
 * `session.profile.tmz` from crashing date formatting on the render path.
 */
export function getValidTimeZone(timeZone?: string): string | undefined {
  const trimmed = timeZone?.trim();
  if (!trimmed) {
    return undefined;
  }
  try {
    // Probe Intl directly — cheaper than formatting and matches the same
    // validation date-fns-tz performs internally.
    // eslint-disable-next-line no-new -- side-effect probe; we only care whether the constructor throws.
    new Intl.DateTimeFormat(undefined, { timeZone: trimmed });
    return trimmed;
  } catch {
    return undefined;
  }
}

/**
 * Always returns a usable IANA string: the input if valid, else the viewer's
 * device timezone, else "UTC" as a final defensive fallback.
 *
 * `Intl.DateTimeFormat().resolvedOptions().timeZone` is guaranteed by spec to
 * be valid in evergreen browsers — the "UTC" branch should be unreachable in
 * practice but guards against a future runtime surprise.
 */
export function getSafeTimeZone(timeZone?: string): string {
  const valid = getValidTimeZone(timeZone);
  if (valid) {
    return valid;
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}
